import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { recordCallFlow } from '../../../debug/call-flow-log'
import {
  getCachedRouteSnapshot,
  getNativeRouteSnapshot,
  setNativeOutputDevice,
  setNativeSpeakerphone,
  showNativeRoutePicker,
  subscribeNativeRouteSnapshot,
  type CallAudioOutputDevice,
  type CallAudioRouteSnapshot,
} from '../../../services/voice-call/native-audio-route'
import { setNativeIosMediaSpeaker } from '../../../services/voice-call/native-ios-media'
import { patchVoiceCallSession } from '../../../stores/voice-call-store'
import { displayNameForAudioRoute } from '../../../features/calls/utils/call-audio-route-labels'
import { callAudioRouteArbiter, type UserAudioOutput } from '../../../utils/call-audio-route-arbiter'
import {
  acceptsRouteTap,
  displayedRouteKind,
  ROUTE_PICK_SETTLE_MS,
} from '../utils/route-tap-guard'
import { TransientSpeakerGate } from '../utils/transient-speaker-gate'
import { isDesktopWeb, isNativeApp } from '../../../utils/platform'

export type RoutePickerOption = CallAudioOutputDevice & { selected: boolean }

const EXTERNAL_KINDS = new Set<CallAudioOutputDevice['kind']>(['bluetooth', 'wired', 'car'])

function webOutputSupported(): boolean {
  if (typeof HTMLMediaElement === 'undefined') return false
  return typeof (HTMLMediaElement.prototype as { setSinkId?: unknown }).setSinkId === 'function'
}

async function listWebOutputDevices(): Promise<CallAudioOutputDevice[]> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) return []
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    return devices
      .filter((d) => d.kind === 'audiooutput')
      .map((d, i) => ({
        id: d.deviceId || `web-out-${i}`,
        name: d.label || (d.deviceId === 'default' ? 'Default' : `Output ${i + 1}`),
        kind: /speaker/i.test(d.label) ? 'speaker' : 'bluetooth',
      }))
  } catch {
    return []
  }
}

async function applyWebSink(deviceId: string): Promise<boolean> {
  if (typeof document === 'undefined') return false
  const audio = document.querySelector<HTMLAudioElement>('audio[data-call-remote="true"]')
  const el = audio ?? document.querySelector('audio')
  if (!el || typeof (el as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }).setSinkId !== 'function') {
    return false
  }
  try {
    await (el as HTMLAudioElement & { setSinkId: (id: string) => Promise<void> }).setSinkId(deviceId)
    return true
  } catch {
    return false
  }
}

/** Structural equality so a fresh-but-identical snapshot never re-renders (chip flicker). */
function sameSnapshot(a: CallAudioRouteSnapshot | null, b: CallAudioRouteSnapshot | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  if (a.kind !== b.kind || a.name !== b.name || a.availableExternal !== b.availableExternal) {
    return false
  }
  if ((a.devices?.length ?? 0) !== (b.devices?.length ?? 0)) return false
  return (a.devices ?? []).every((d, i) => {
    const other = b.devices?.[i]
    return other && d.id === other.id && d.name === other.name && d.kind === other.kind
  })
}

/**
 * Live in-call (and ringing/connecting) audio-route state + picker.
 *
 * Event-driven: subscribes to native `routeChanged` snapshots (plus the
 * controller-fed Telecom/CallKit route events) instead of the old 2s polling,
 * with a slow safety-net refresh. Snapshot updates are structurally compared
 * so route churn never re-renders (and never flickers) the control.
 */
export function useCallAudioRoute(active: boolean): {
  /** Live output kind (earpiece / speaker / bluetooth / wired / car). */
  kind: CallAudioOutputDevice['kind']
  /** Display label for the live output ("Speaker", "AirPods Pro", "Car"). */
  label: string
  /** Audio is currently ON a non-builtin device (earbuds / car / wired). */
  externalActive: boolean
  /** A non-builtin device is attached (routed to or not). */
  externalAvailable: boolean
  /** Live route is the loud speaker. */
  speakerOn: boolean
  /** Anything to render at all (web hides without setSinkId support). */
  supported: boolean
  pickerOpen: boolean
  setPickerOpen: (open: boolean) => void
  options: RoutePickerOption[]
  select: (device: CallAudioOutputDevice) => Promise<void>
  /** Fast path: flip speaker ⇄ earpiece without opening the picker. */
  toggleBuiltin: () => Promise<void>
  openSystemPicker: () => Promise<boolean>
} {
  const [snapshot, setSnapshot] = useState<CallAudioRouteSnapshot | null>(() =>
    getCachedRouteSnapshot(),
  )
  const [webDevices, setWebDevices] = useState<CallAudioOutputDevice[]>([])
  const [webSelectedId, setWebSelectedId] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pendingPick, setPendingPick] = useState<{
    kind: CallAudioOutputDevice['kind']
    atMs: number
  } | null>(null)
  const lastPickAtRef = useRef<number | null>(null)
  const snapshotRef = useRef(snapshot)
  snapshotRef.current = snapshot
  const speakerGateRef = useRef(new TransientSpeakerGate())
  const [, setSpeakerGateTick] = useState(0)

  const adoptSnapshot = useCallback((next: CallAudioRouteSnapshot | null) => {
    if (!next || sameSnapshot(snapshotRef.current, next)) return
    setSnapshot(next)
  }, [])

  const refresh = useCallback(async () => {
    if (isNativeApp()) {
      adoptSnapshot(await getNativeRouteSnapshot())
      return
    }
    if (webOutputSupported()) {
      setWebDevices(await listWebOutputDevices())
    }
  }, [adoptSnapshot])

  useEffect(() => {
    if (!active) {
      setPickerOpen(false)
      return
    }
    void refresh()
    if (isNativeApp()) {
      const unsubscribe = subscribeNativeRouteSnapshot(adoptSnapshot)
      // Safety net for shells whose plugin doesn't emit routeChanged (old APK,
      // all current iOS builds — kept fresh via noteHardwareRoute otherwise).
      const timer = window.setInterval(() => void refresh(), 10_000)
      return () => {
        unsubscribe()
        window.clearInterval(timer)
      }
    }
    return undefined
  }, [active, adoptSnapshot, refresh])

  // Re-list devices while the picker is open (a device may connect mid-pick).
  useEffect(() => {
    if (!pickerOpen) return
    void refresh()
  }, [pickerOpen, refresh])

  // The pick shows as selected until the OS route reports it (or settles).
  useEffect(() => {
    if (!pendingPick) return
    if (snapshot?.kind === pendingPick.kind) {
      setPendingPick(null)
      return
    }
    const timer = window.setTimeout(() => setPendingPick(null), ROUTE_PICK_SETTLE_MS)
    return () => window.clearTimeout(timer)
  }, [pendingPick, snapshot])

  // Re-evaluate a held-back speaker report once its hold expires — without
  // this a genuinely persisting speaker route would never paint (the gate is
  // consulted at render time only).
  useEffect(() => {
    const delay = speakerGateRef.current.nextCheckDelayMs(Date.now())
    if (delay === null) return
    const timer = window.setTimeout(() => setSpeakerGateTick((t) => t + 1), delay + 10)
    return () => window.clearTimeout(timer)
  })

  const nativeDevices = useMemo<CallAudioOutputDevice[]>(
    () =>
      snapshot?.devices?.length
        ? snapshot.devices
        : /* fallback builtins when the native list API is missing (old shell) */ [
            { id: 'earpiece', name: 'Earpiece', kind: 'earpiece' },
            { id: 'speaker', name: 'Speaker', kind: 'speaker' },
          ],
    [snapshot],
  )

  const options: RoutePickerOption[] = isNativeApp()
    ? nativeDevices.map((d) => ({
        ...d,
        name: displayNameForAudioRoute(d.kind, d.name),
        selected: snapshot
          ? d.kind === snapshot.kind &&
            (d.name === snapshot.name ||
              nativeDevices.filter((x) => x.kind === snapshot.kind).length === 1)
          : false,
      }))
    : webDevices.map((d) => ({
        ...d,
        selected: webSelectedId ? d.id === webSelectedId : d.id === 'default',
      }))

  const liveKind = snapshot?.kind ?? (callAudioRouteArbiter.desiredSpeakerOn() ? 'speaker' : 'earpiece')
  const rawKind = displayedRouteKind(pendingPick, liveKind, Date.now())
  // Speaker-flash fix: a loud-speaker report the arbiter does not want and the
  // user did not pick is a connect-time transient (CallKit/Telecom first route,
  // ringtone leftover) — hold it back until it persists. Display only.
  const kind = speakerGateRef.current.displayKind(
    rawKind,
    callAudioRouteArbiter.desiredSpeakerOn(),
    pendingPick !== null,
    Date.now(),
  )
  const label =
    kind !== liveKind
      ? displayNameForAudioRoute(kind, kind === 'speaker' ? 'Speaker' : 'Earpiece')
      : snapshot
        ? displayNameForAudioRoute(snapshot.kind, snapshot.name)
        : callAudioRouteArbiter.desiredSpeakerOn()
          ? 'Speaker'
          : 'Earpiece'
  const externalActive = EXTERNAL_KINDS.has(kind)
  const externalAvailable =
    externalActive ||
    snapshot?.availableExternal === true ||
    nativeDevices.some((d) => EXTERNAL_KINDS.has(d.kind))

  const supported = (() => {
    if (!active) return false
    if (isNativeApp()) return true
    if (isDesktopWeb()) return webOutputSupported() && webDevices.length > 0
    return webOutputSupported()
  })()

  const select = useCallback(
    async (device: CallAudioOutputDevice) => {
      const nowMs = Date.now()
      if (!acceptsRouteTap(lastPickAtRef.current, nowMs)) {
        recordCallFlow('route:userSelect:ignored', {
          kind: device.kind,
          sinceLastMs: nowMs - (lastPickAtRef.current ?? nowMs),
        })
        return
      }
      lastPickAtRef.current = nowMs
      const output: UserAudioOutput =
        device.kind === 'speaker' ? 'speaker' : device.kind === 'earpiece' ? 'earpiece' : 'external'
      callAudioRouteArbiter.userSelect(output)
      patchVoiceCallSession({ isSpeakerOn: output === 'speaker' })
      recordCallFlow('route:userSelect', { id: device.id, kind: device.kind, name: device.name })
      setPickerOpen(false)
      if (isNativeApp()) {
        setPendingPick({ kind: device.kind, atMs: nowMs })
        // ONE apply: device pin when the shell supports it, else the builtin
        // speaker/earpiece pin. (The old triple apply — byId + setSpeakerphone
        // + applyDesired — re-armed native reassert bursts on every pick.)
        // Builtins use the semantic ids so native takes the forceBuiltin path.
        const pinId =
          device.kind === 'speaker' || device.kind === 'earpiece' ? device.kind : device.id
        const byId = await setNativeOutputDevice(pinId)
        if (!byId && output !== 'external') {
          await setNativeSpeakerphone(output === 'speaker')
        }
        void setNativeIosMediaSpeaker(output === 'speaker')
        void refresh()
        return
      }
      const ok = await applyWebSink(device.id)
      if (ok) setWebSelectedId(device.id)
    },
    [refresh],
  )

  const toggleBuiltin = useCallback(async () => {
    const wantSpeaker = kind !== 'speaker'
    const target =
      nativeDevices.find((d) => d.kind === (wantSpeaker ? 'speaker' : 'earpiece')) ??
      ({ id: wantSpeaker ? 'speaker' : 'earpiece', name: '', kind: wantSpeaker ? 'speaker' : 'earpiece' } as CallAudioOutputDevice)
    await select(target)
  }, [kind, nativeDevices, select])

  const openSystemPicker = useCallback(async () => {
    const shown = await showNativeRoutePicker()
    if (shown) setPickerOpen(false)
    return shown
  }, [])

  return {
    kind,
    label,
    externalActive,
    externalAvailable,
    speakerOn: kind === 'speaker',
    supported,
    pickerOpen,
    setPickerOpen,
    options,
    select,
    toggleBuiltin,
    openSystemPicker,
  }
}
